import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Divider, Grid, Button as JoyButton, FormControl, FormLabel, Sheet, Chip } from "@mui/joy";
import { UploadCloud, ArrowLeft, FilePlus, FileText, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useContractTemplate } from "../../../../hooks/query";
import { TEMPLATE_TYPES, AVAILABLE_SCOPES } from "../../constants";
import Breadcrumb from "../Breadcrumb";
import { Button, Alert, TextField, IconButton } from "../../../../shared";
import FormSelect from "../../../../shared/FormSelect";
import PdfPreview from "./PdfPreview";

export default function Create() {
    const navigate = useNavigate();
    const { useParseFields, useCreate } = useContractTemplate();
    const parseFields = useParseFields();
    const createTemplate = useCreate();

    const [file, setFile] = useState<File | null>(null);
    const [fields, setFields] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [activeField, setActiveField] = useState<string | null>(null);

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            type: TEMPLATE_TYPES[0].value,
            mapping: {} as Record<string, string>,
        }
    });

    const { handleSubmit, setValue, formState: { isValid }, control } = methods;

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            if (selectedFile.type !== "application/pdf") {
                setError("Please upload a valid PDF file.");
                return;
            }
            setFile(selectedFile);
            setError(null);
            
            try {
                const data = await parseFields.mutateAsync(selectedFile);
                if (data.fields && data.fields.length > 0) {
                    setFields(data.fields);
                    
                    const newMapping: Record<string, string> = {};
                    data.fields.forEach((f: string) => newMapping[f] = "");
                    setValue("mapping", newMapping);
                } else {
                    setError("No AcroForm fields detected in this PDF.");
                }
            } catch (err: any) {
                setError("Failed to parse PDF fields.");
            }
        }
    };

    const onSubmit = async (data: any) => {
        if (!file) {
            setError("Please upload a PDF file.");
            return;
        }

        try {
            await createTemplate.mutateAsync({
                name: data.name,
                type: data.type,
                file,
                mappingConfig: data.mapping
            });
            navigate("/admin/contract-template/list");
        } catch (err: any) {
            setError("Failed to create template.");
        }
    };

    // Scroll to the mapped field in the sidebar when activeField changes
    useEffect(() => {
        if (activeField) {
            const element = document.getElementById(`mapping-field-${activeField}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [activeField]);

    return (
        <Box flex={1} display="flex" flexDirection="column" sx={{ overflow: 'hidden' }}>
            <Breadcrumb />
            <Divider />

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden', margin: 0, padding: 0 }}>
                    <Box sx={{ flex: 1, display: 'flex', width: '100%', minHeight: 0 }}>
                        
                        {/* LEFT PANE: PDF PREVIEW */}
                        <Box sx={{ flex: 1, height: '100%', overflow: 'hidden', bgcolor: 'background.level1', display: 'flex', flexDirection: 'column' }}>
                            {file ? (
                                <PdfPreview 
                                    file={file} 
                                    onFieldClick={setActiveField}
                                    activeField={activeField} 
                                    mapping={methods.watch("mapping")}
                                />
                            ) : (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                                    <Box 
                                        component="label"
                                        sx={{
                                            border: '1px dashed',
                                            borderColor: 'neutral.300',
                                            bgcolor: 'background.surface',
                                            borderRadius: '8px',
                                            p: 6,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            width: '100%',
                                            maxWidth: 400,
                                            '&:hover': {
                                                bgcolor: 'neutral.50',
                                                borderColor: 'neutral.400'
                                            }
                                        }}
                                    >
                                        <UploadCloud size={32} color="#64748b" style={{ marginBottom: '16px' }} />
                                        <Typography level="title-md" sx={{ fontWeight: 500 }} mb={0.5}>
                                            Click to upload or drag and drop
                                        </Typography>
                                        <Typography level="body-xs" color="neutral" textAlign="center">
                                            PDF form up to 10MB
                                        </Typography>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* RIGHT PANE: SIDEBAR MAPPING */}
                        <Box sx={{ width: { xs: '100%', md: 450, lg: 500 }, height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid', borderColor: 'divider', bgcolor: 'background.surface' }}>
                            <Box p={3} sx={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                
                                <TextField
                                    name="name"
                                    label="Template Name"
                                    placeholder="e.g., Master Contract 2024"
                                    rules={{ required: "Name is required" }}
                                />
                                
                                <FormControl>
                                    <FormLabel sx={{ textTransform: "uppercase", fontSize: "12px", fontWeight: "xl", letterSpacing: "0.5px" }}>
                                        Document Type
                                    </FormLabel>
                                    <Box>
                                        <Chip size="md" color="primary" variant="soft">Contract</Chip>
                                    </Box>
                                </FormControl>

                                {file && (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between', 
                                        bgcolor: 'primary.50', 
                                        p: 1.5, 
                                        borderRadius: '4px', 
                                        border: '1px solid', 
                                        borderColor: 'primary.200' 
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <FileText size={20} color="#0284c7" />
                                            <Typography level="body-sm" sx={{ fontWeight: 600, color: 'primary.900' }}>
                                                {file.name}
                                            </Typography>
                                        </Box>
                                        <IconButton size="sm" variant="soft" color="danger" onClick={() => {
                                            setFile(null);
                                            setFields([]);
                                            setActiveField(null);
                                            methods.setValue("mapping", {});
                                        }}>
                                            <Trash2 size={16} />
                                        </IconButton>
                                    </Box>
                                )}

                                {fields.length > 0 && (
                                    <Box mt={1}>
                                        <Typography level="title-md" mb={0.5}>Map PDF Fields</Typography>
                                        <Typography level="body-xs" color="neutral" mb={2}>
                                            Click a field on the PDF preview or configure it below.
                                        </Typography>
                                        
                                        <Box sx={{ display: "flex", flexDirection: "column", border: '1px solid', borderColor: 'divider', borderRadius: '4px', overflow: 'hidden' }}>
                                            {fields.map((field, index) => (
                                                <Box 
                                                    key={field} 
                                                    id={`mapping-field-${field}`}
                                                    onClick={() => setActiveField(field)}
                                                    sx={{ 
                                                        p: 1, 
                                                        px: 1.5,
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'space-between',
                                                        gap: 1.5,
                                                        borderBottom: index < fields.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider',
                                                        bgcolor: activeField === field ? 'primary.50' : 'background.surface',
                                                        transition: 'all 0.2s',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: activeField === field ? 'primary.100' : 'neutral.50'
                                                        }
                                                    }}
                                                >
                                                    <Typography 
                                                        level="body-xs" 
                                                        sx={{ 
                                                            fontWeight: activeField === field ? 700 : 500, 
                                                            color: activeField === field ? 'primary.700' : 'text.primary',
                                                            flex: 1, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap' 
                                                        }}
                                                    >
                                                        {field}
                                                    </Typography>
                                                    <Box sx={{ width: '60%' }}>
                                                        <FormSelect
                                                            name={`mapping.${field}`}
                                                            label=""
                                                            size="sm"
                                                            options={[
                                                                { label: "Leave blank", value: "" },
                                                                ...AVAILABLE_SCOPES,
                                                            ]}
                                                        />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* FIXED BOTTOM ACTIONS */}
                            <Box p={2.5} sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.surface' }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="neutral"
                                        startDecorator={<ArrowLeft size={18} />}
                                        onClick={() => navigate("/admin/contract-template/list")}
                                        disabled={createTemplate.isPending}
                                        sx={{ flex: 1 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="soft"
                                        color="primary"
                                        startDecorator={<FilePlus size={18} />}
                                        loading={createTemplate.isPending}
                                        disabled={!isValid || !file || fields.length === 0 || createTemplate.isPending}
                                        sx={{ flex: 1 }}
                                    >
                                        Create Template
                                    </Button>
                                </Box>
                                {error && <Box mt={2}><Alert errorMessage={error} /></Box>}
                            </Box>
                        </Box>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}
